import { ScrollDirection } from '../../domain/direction';
import { Page } from './page';
import type { CachingDataSourceQuery } from './caching-data-source-query';
import type { RequestablePage } from './immutable-paginated-cache';

/**
 * ProactiveQueryResolver
 *
 * Sometimes it is reasonable to load search result in advance even though the requested page is
 * either cached or was already requested. For instance if the data are cached for pages from 1 till 10,
 * and user is scrolling down 1 -> 2 -> 3 ... than it is reasonable to load data for pages 11-20
 * when user reach some page close enough to the end of cache data (for instance page 5).
 */
export interface ProactiveQueryResolver<Q extends CachingDataSourceQuery> {

  /**
   * Estimate search query in advance.
   *
   * @param {Q} previousQuery
   * @param {Q} newQuery
   * @param {Array<boolean>} requestedPages
   *
   * @returns {Q} Returns estimated query, undefined if no reason to run any advanced data search (for instance
   * when enough data are cached)
   */
  estimateQueryInAdvance(previousQuery: Q, newQuery: Q, requestedPages: RequestablePage): Q | undefined;
}

export class DirectionalProactiveQueryResolver<Q extends CachingDataSourceQuery> implements ProactiveQueryResolver<Q> {

  private static readonly DEFAULT_PROXIMITY_FACTOR: number = 0.7;

  constructor(private proximityFactor: number = DirectionalProactiveQueryResolver.DEFAULT_PROXIMITY_FACTOR) {
  }

  estimateQueryInAdvance(query: Q, newQuery: Q, requestedPages: RequestablePage): Q | undefined {
    let estimatedQuery: Q | undefined;
    const scrollingDirection = this.calculateScrollingDirection(query, newQuery);
    const firstNonCachedPage = this.getFirstNonCachedPage(scrollingDirection, query, requestedPages);
    if (firstNonCachedPage !== -1 && this.isInProximity(firstNonCachedPage, newQuery)) {
      const page = new Page(query.getPage().getPageSize(), firstNonCachedPage, query.getPage().getTotalRecords());
      estimatedQuery = query.clone(page, scrollingDirection) as Q;
    }
    return estimatedQuery;
  }

  calculateScrollingDirection(previousQuery: Q, newQuery: Q): ScrollDirection {
    let scrollingDirection: ScrollDirection = ScrollDirection.UNKNOWN;
    if (previousQuery.getPage().getPageNumber() < newQuery.getPage().getPageNumber()) {
      scrollingDirection = ScrollDirection.DOWN;
    } else if (previousQuery.getPage().getPageNumber() > newQuery.getPage().getPageNumber()) {
      scrollingDirection = ScrollDirection.UP;
    }
    return scrollingDirection;
  }

  isInProximity(firstNonCachedPage: number, query: Q): boolean {
    const closenessMagnitude = Math.abs(firstNonCachedPage - query.getPage().getPageNumber());
    return closenessMagnitude * query.getPage().getPageSize() <= query.getSearchSize() * this.proximityFactor;
  }

  getFirstNonCachedPage(scrollingDirection: ScrollDirection, query: Q, requestedPages: RequestablePage): number {
    let found = false;
    let pageNumber;
    if (scrollingDirection === ScrollDirection.UP || scrollingDirection === ScrollDirection.DOWN) {
      pageNumber = query.getPage().getPageNumber();
      while (!found && pageNumber >= 0 && pageNumber < query.getPage().getTotalPages()) {
        pageNumber += scrollingDirection;
        found = !requestedPages.isPageRequested(pageNumber);
      }
    }
    return found ? pageNumber : -1;
  }
}
